import React, { useState } from "react";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { COLORS } from "./constants";
import { panelSx, sectionSubtleSx, sectionTitleSx } from "./styles";
import { formatBytes } from "./utils";
import { ConfirmDialog } from "./ConfirmDialog";

// ─────────────────────────────────────────────────────────────────────────────
// UpNextPanel — scheduled MP4 streams that haven't started yet
//
// Scheduling an MP4 used to leave no trace outside the MP4 tab: the dashboard
// never said that something would go out on its own at 18:00 on Friday. This
// card does, from the same /api/stream-status/file-jobs the MP4 tab polls, and
// lets the user cancel one (asking first). It is hidden when nothing is
// scheduled, so RTMP-only users never see an empty box.
// ─────────────────────────────────────────────────────────────────────────────

const SHOWN = 3;

function whenLabel(iso, now) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "Time not set";
  const time = at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const day = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((day(at) - day(new Date(now))) / 86_400_000);
  if (diffDays === 0) return `Today ${time}`;
  if (diffDays === 1) return `Tomorrow ${time}`;
  if (diffDays > 1 && diffDays < 7) return `${at.toLocaleDateString([], { weekday: "long" })} ${time}`;
  return `${at.toLocaleDateString([], { day: "numeric", month: "short" })}, ${time}`;
}

function inLabel(iso, now) {
  const mins = Math.round((new Date(iso).getTime() - now) / 60_000);
  if (!Number.isFinite(mins)) return "";
  if (mins <= 0) return "starting now";
  if (mins < 60) return `in ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 48) return `in ${h} h${mins % 60 ? ` ${mins % 60} min` : ""}`;
  return `in ${Math.round(h / 24)} days`;
}

// "today 18:00", "tomorrow 09:30", "on Friday 18:00", "on 14 Oct, 18:00"
function whenPhrase(iso, now) {
  const when = whenLabel(iso, now);
  return /^(Today|Tomorrow)/.test(when) ? when.charAt(0).toLowerCase() + when.slice(1) : `on ${when}`;
}

export function UpNextPanel({ jobs, onCancel, onOpenScheduler }) {
  const [target, setTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const now = Date.now();

  const upcoming = (jobs || [])
    .filter((j) => j.status === "scheduled")
    .sort((a, b) => Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt));
  if (!upcoming.length) return null;

  const name = (job) => job.originalName || job.fileName || "Video";

  return (
    <Box sx={panelSx}>
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
          <Box sx={{
            width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
            bgcolor: "rgba(43,189,175,0.12)", border: "1px solid rgba(43,189,175,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <EventIcon sx={{ color: COLORS.cyan, fontSize: 19 }} />
          </Box>
          <Box minWidth={0}>
            <Typography sx={sectionTitleSx}>Up next</Typography>
            <Typography sx={sectionSubtleSx}>
              {upcoming.length} scheduled stream{upcoming.length === 1 ? "" : "s"}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Stack spacing={1}>
        {upcoming.slice(0, SHOWN).map((job, i) => {
          const dests = job.destinations?.length || 0;
          return (
            <Box key={job.id} sx={{
              display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: "14px",
              border: `1px solid ${i === 0 ? "rgba(43,189,175,0.35)" : COLORS.border}`,
              bgcolor: i === 0 ? "rgba(24,165,152,0.08)" : COLORS.panelSoft
            }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: COLORS.text, fontWeight: 700, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>
                  {whenLabel(job.scheduledAt, now)}
                  <Box component="span" sx={{ ml: 1, color: COLORS.cyan, fontWeight: 600, fontSize: 12 }}>
                    {inLabel(job.scheduledAt, now)}
                  </Box>
                </Typography>
                <Typography noWrap title={name(job)} sx={{ color: COLORS.textSoft, fontSize: 13, mt: 0.25 }}>
                  {name(job)}
                </Typography>
                <Typography sx={{ color: COLORS.muted, fontSize: 12, mt: 0.25 }}>
                  {[
                    dests ? `to ${dests} destination${dests === 1 ? "" : "s"}` : null,
                    job.fileSize ? formatBytes(job.fileSize) : null
                  ].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
              <Tooltip title="Cancel this stream">
                <IconButton
                  aria-label={`Cancel the stream of ${name(job)} at ${whenLabel(job.scheduledAt, now)}`}
                  onClick={() => { setTarget(job); setOpen(true); }}
                  sx={{ width: 36, height: 36, color: COLORS.muted, border: `1px solid ${COLORS.border}`, borderRadius: "10px",
                    "&:hover": { color: COLORS.red, borderColor: "rgba(229,72,77,0.45)", bgcolor: COLORS.dangerSoft } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Stack>

      <Button onClick={onOpenScheduler} endIcon={<ArrowForwardIcon />}
        sx={{ mt: 1.5, textTransform: "none", fontWeight: 600, color: COLORS.cyan, px: 1 }}>
        {upcoming.length > SHOWN ? `See all ${upcoming.length} in the MP4 scheduler` : "Open the MP4 scheduler"}
      </Button>

      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={() => target && onCancel(target)}
        icon={<EventBusyIcon />}
        title="Cancel this scheduled stream?"
        body={target ? `"${name(target)}" will not go out ${whenPhrase(target.scheduledAt, now)}. The video stays in your library.` : ""}
        cancelLabel="Keep it"
        confirmLabel="Cancel stream"
      />
    </Box>
  );
}
